import { formatAdminDate } from '../../utils/datetime';

/**
 * Pure formatting utilities for the Backtest page.
 * No React dependency — these are plain functions operating
 * on primitive values. Extracted to keep BacktestPage.jsx focused
 * on layout and state management.
 */

/**
 * Render the selected date range as a compact "30 May → 6 Jun 2026"
 * string for the header pill. We drop the year on the start date
 * when both ends fall in the same calendar year so the pill stays
 * short — the year is always shown on the end date so there's no
 * ambiguity if someone backtests across New Year.
 */
export const formatRange = (startStr, endStr) => {
  const s = new Date(startStr);
  const e = new Date(endStr);
  if (isNaN(s.getTime()) || isNaN(e.getTime())) return '';
  const startFmt = formatAdminDate(startStr, '');
  const endFmt = formatAdminDate(endStr, '');
  return `${startFmt} → ${endFmt}`;
};

/**
 * Format a condition's left-side value for the breakdown panel.
 * Time conditions: convert the candle's ISO UTC timestamp to IST
 * HH:MM via the browser's native Intl with the Asia/Kolkata zone
 * — no manual timezone math, no duplication of the engine's IST
 * logic. Indicator / Static conditions: format the actualValue
 * number to 2 decimals, matching the table's existing precision.
 */
export const formatCondActual = (c) => {
  if (c?.category === 'Time') {
    if (!c.candleTimestamp) return '—';
    try {
      return new Intl.DateTimeFormat('en-IN', {
        timeZone: 'Asia/Kolkata',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(new Date(c.candleTimestamp));
    } catch {
      return '—';
    }
  }
  if (c?.actualValue == null) return '—';
  return Number(c.actualValue).toFixed(2);
};

/**
 * Format a condition's right-side value. Time conditions: the
 * user typed an HH:MM string, just pass through. Otherwise format
 * the compareValue number.
 */
export const formatCondCompare = (c) => {
  if (c?.category === 'Time') return c.targetTimeRaw || '—';
  if (c?.compareValue == null) return '—';
  return Number(c.compareValue).toFixed(2);
};

/**
 * Format a rupee amount with optional signed prefix. Returns
 * '—' for null/undefined/NaN so empty cells stay visually
 * distinct from real zeros. Uses the en-IN locale for the
 * lakh/crore comma grouping the user expects.
 */
export const formatRupees = (value, { showSign = true } = {}) => {
  if (value == null || !Number.isFinite(value)) return '—';
  const formatted = Math.abs(value).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (value === 0) return `₹${formatted}`;
  if (!showSign) return `₹${formatted}`;
  return value < 0 ? `-₹${formatted}` : `+₹${formatted}`;
};

/**
 * Format a percentage to 2 decimal places (e.g. 55.56%). Null
 * input collapses to '—'. Keeps the trailing zero so the column
 * width doesn't shift between e.g. "10.00%" and "10%".
 */
export const formatPct = (value) => {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${Number(value).toFixed(2)}%`;
};

/**
 * Format profit factor with a sentinel for the "no losses"
 * case (backend returns null). A true ∞ symbol communicates
 * "no losing trades to divide by" more cleanly than a number.
 */
export const formatProfitFactor = (value) => {
  if (value == null) return '∞';
  if (!Number.isFinite(value)) return '∞';
  return Number(value).toFixed(2);
};

/**
 * Pick the right colour class for a P&L number. Positive →
 * green, negative → red, zero/null → neutral. The caller
 * passes the CSS-modules styles object so this helper stays
 * decoupled from any specific stylesheet.
 */
export const pnlColourClass = (value, styles) => {
  if (value == null || value === 0) return styles.pnlNeutral;
  return value > 0 ? styles.pnlPositive : styles.pnlNegative;
};

/**
 * Build the hover tooltip text for an EXIT row's Net P&L cell.
 * Direction-aware: works correctly for both long (BUY action
 * strategy) and short (SELL action strategy) trades, so the
 * math shown in the tooltip always matches the displayed P&L.
 *
 * Mapping (recall: EXIT row's action is flipped from the
 * strategy's action):
 *   - m.action === 'SELL' → strategy is BUY (long): we bought
 *     at the ENTRY price and sold at the EXIT price
 *   - m.action === 'BUY'  → strategy is SELL (short): we sold
 *     at the ENTRY price and bought back at the EXIT price
 *
 * Returns undefined when the row isn't an EXIT, when there's
 * no P&L to show, or when any expected field is missing — so
 * the title attribute simply doesn't render (no broken hover).
 */
export const buildPnLTooltip = (m) => {
  if (!m || m.signal_type !== 'EXIT') return undefined;
  if (typeof m.net_pl !== 'number') return undefined;
  if (!m.quantity) return undefined;
  if (typeof m.entry_price !== 'number') return undefined;
  if (typeof m.exit_price !== 'number') return undefined;
  // Direction detection: m.action on an EXIT row is the side of
  // THIS leg. SELL exit means long strategy (entered by BUY,
  // exits by SELL). BUY exit means short strategy.
  const isLong = m.action === 'SELL';
  const soldPrice = isLong ? m.exit_price : m.entry_price;
  const boughtPrice = isLong ? m.entry_price : m.exit_price;
  // When backend provided post-brokerage totals (sizing + brokerage
  // both configured at the time of trade) we render the specified
  // formula verbatim. Otherwise fall back to the gross-price form
  // — same tooltip the old version showed.
  const sellTotal = isLong ? m.total_amount : m.entry_total_amount;
  const buyTotal  = isLong ? m.entry_total_amount : m.total_amount;
  if (typeof sellTotal === 'number' && typeof buyTotal === 'number') {
    return `Sold ${m.quantity} @ ₹${soldPrice.toFixed(2)} → ${formatRupees(sellTotal)} − Bought ${m.quantity} @ ₹${boughtPrice.toFixed(2)} → ${formatRupees(buyTotal)} = ${formatRupees(m.net_pl)}`;
  }
  return `Sold ${m.quantity} @ ₹${soldPrice.toFixed(2)} − Bought ${m.quantity} @ ₹${boughtPrice.toFixed(2)} = ${formatRupees(m.net_pl)}`;
};

/**
 * Generate a human-readable tooltip for an indicator column
 * header. Recognises the SMA(N) pattern explicitly so the
 * tooltip can name the period and timeframe. Falls back to a
 * generic message for any other indicator added later.
 */
export const indicatorTooltipText = (col) => {
  if (!col?.label) return null;
  const smaMatch = col.label.match(/^SMA\s*\((\d+)\)/i);
  if (smaMatch) {
    const period = smaMatch[1];
    const tf = col.timeframe || '1m';
    return `Simple Moving Average — the average closing price across the last ${period} candles on the ${tf} timeframe.`;
  }
  return `${col.label} indicator value at the close of this candle.`;
};
